import { ReactNode, useState } from 'react'

import { AstraParagraph, getOnlyDate } from '~/astra'
import { useStore } from '~/store/store'
import { useActiveLocations, usePractitionerScheduleLocations, usePractitionerStatusDefinitions } from '~/storm'
import { sortStatusDefinitions } from '~/utils/surgeryActivities'

import { arrayHasAtLeastOneElement, DatePractitionerCell, NotePreview, useImportActivityLogs } from '../../shared'
import { PractitionerBottomBar } from '../../shared/BottomBar'
import { ActivityLog } from './ActivityLog/ActivityLog'
import { Header } from './Header'
import { LocationTagsWithStaticState } from './Locations/LocationTagsWithStaticState'
import { PractitionerNote } from './PractitionerNote'
import { StatusDefinitionTags } from './Statuses/StatusDefinitionTags'

const ModalElement = ({ label, secondaryLabel, children }: { label: string; secondaryLabel?: string; children?: ReactNode }) => (
    <div className="pointer-events-auto border-b px-3 pb-3 pt-2">
        <div className="flex items-center justify-between">
            <AstraParagraph>{label}</AstraParagraph>
            {secondaryLabel && <AstraParagraph className="text-muted">{secondaryLabel}</AstraParagraph>}
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">{children}</div>
    </div>
)

type Props = {
    selectedCells: DatePractitionerCell[]
}

export const PractitionerScheduleModal = ({ selectedCells }: Props) => {
    const departmentKey = useStore(state => state.appFilters.departmentKey)

    const activeLocations = useActiveLocations({})
    const statusDefinitions = usePractitionerStatusDefinitions({ include: { departments: true } })
    const orderedPractitionerStatusDefinitions = sortStatusDefinitions(statusDefinitions)

    const allScheduleLocationsByCell = usePractitionerScheduleLocations({
        where: {
            OR: selectedCells.map(({ date, practitionerId }) => ({ scheduled_date: getOnlyDate(date), practitioner_id: practitionerId })),
        },
    })
    const allPractitionerScheduleLocationsByDate = usePractitionerScheduleLocations({
        where: {
            scheduled_date: { in: selectedCells.map(({ date }) => getOnlyDate(date)) },
        },
    })

    const firstCell = selectedCells.at(0)
    const singleCell = selectedCells.length === 1

    const availableLocations = activeLocations.filter(
        location => !allPractitionerScheduleLocationsByDate.some(({ location_id }) => location_id === location.id)
    )
    const unavailableLocations = activeLocations.filter(location =>
        allPractitionerScheduleLocationsByDate.some(({ location_id }) => location_id === location.id)
    )

    const [showNote, setShowNote] = useState(false)
    const [showActivityLog, setShowActivityLog] = useState(false)

    const { isLoading } = useImportActivityLogs(firstCell)

    if (showNote && firstCell && singleCell) return <PractitionerNote cell={firstCell} hideNote={() => setShowNote(false)} />
    if (showActivityLog && firstCell && singleCell) return <ActivityLog cell={firstCell} hideLog={() => setShowActivityLog(false)} isLoading={isLoading} />

    return (
        <div className="pointer-events-none relative flex w-72 flex-col pt-3" tabIndex={0}>
            <Header selectedCells={selectedCells} />

            <ModalElement label="Aktivitet">
                <StatusDefinitionTags statusDefinitions={orderedPractitionerStatusDefinitions} selectedCells={selectedCells} departmentKey={departmentKey} />
            </ModalElement>

            <ModalElement label="Ledige stuer" secondaryLabel={`${availableLocations.length}/${activeLocations.length}`}>
                <LocationTagsWithStaticState
                    selectedCells={selectedCells}
                    availableLocations={availableLocations}
                    scheduleLocations={allScheduleLocationsByCell}
                />
            </ModalElement>

            <ModalElement label="Stuer med kirurg" secondaryLabel={`${unavailableLocations.length}/${activeLocations.length}`}>
                <LocationTagsWithStaticState
                    selectedCells={selectedCells}
                    availableLocations={unavailableLocations}
                    scheduleLocations={allScheduleLocationsByCell}
                />
            </ModalElement>

            {firstCell && singleCell && <NotePreview cell={firstCell} openNote={() => setShowNote(true)} />}

            {arrayHasAtLeastOneElement(selectedCells) && (
                <PractitionerBottomBar selectedCells={selectedCells} openNote={() => setShowNote(true)} openActivityLog={() => setShowActivityLog(true)} />
            )}
        </div>
    )
}
